import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import * as faqService from "../../services/faq.services";
import Breadcrums from "../../common/breadcrumbs";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import moment from "moment";

import {
  PER_PAGE_OPTIONS,
  STATUS,
  SWAL_SETTINGS,
  THEME_COLORS,
} from "../../../../utils/Constants";
import {
  handleServerValidations,
  hasP,
  TrimText,
} from "../../../../utils/commonfunction";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery, useQueryClient } from "react-query";
import NoRowsOverlay from "../../common/NoRowsOverlay";

ModuleRegistry.registerModules([AllCommunityModule]);

const FaqTable = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const gridRef = useRef();
  const queryClient = useQueryClient();

  const [searchData, setSearchData] = useState("");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("sidebar_link_faq"), url: "" },
  ];

  // ================= FETCH =================
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["faq-list"],
    queryFn: () => faqService.List({ limit: 1000 }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  // ✅ OPTIMISTIC STATUS CHANGE
  const changeStatus = (id, status) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        // 🔥 OPTIMISTIC UPDATE - INSTANT
        if (gridRef.current?.api) {
          const rowNode = gridRef.current.api.getRowNode(id);
          if (rowNode) {
            rowNode.setDataValue('status', status);
          }
        }

        // API call
        faqService.Status({ status }, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
          } else {
            // Revert on failure
            if (gridRef.current?.api) {
              const rowNode = gridRef.current.api.getRowNode(id);
              if (rowNode) {
                rowNode.setDataValue('status', status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
              }
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          // Revert on error
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(id);
            if (rowNode) {
              rowNode.setDataValue('status', status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
            }
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  // 🔥 OPTIMISTIC DELETE - NO REFRESH NEEDED
  const Deletefunction = (data) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        const rowId = data.originalId;

        // 🔥 OPTIMISTIC UPDATE - INSTANTLY REMOVE ROW
        if (gridRef.current?.api) {
          gridRef.current.api.applyTransaction({
            remove: [{ originalId: rowId }]
          });
        }
        gridRef.current.api.refreshCells({ force: true });


        // API Call
        faqService.Delete(rowId).then((res) => {
          if (res.success) {
            Swal.fire({
              icon: "success",
              text: res.message,
              ...SWAL_SETTINGS,
              timer: 1500,
              showConfirmButton: false
            });
          } else {
            // 🔥 REVERT ON FAILURE - ADD ROW BACK
            if (gridRef.current?.api && data) {
              gridRef.current.api.applyTransaction({
                add: [data]
              });
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(res),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          // 🔥 REVERT ON ERROR - ADD ROW BACK
          if (gridRef.current?.api && data) {
            gridRef.current.api.applyTransaction({
              add: [data]
            });
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 90,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("label_question"),
      field: "question",
      flex: 1,
      minWidth: 100,
      tooltipField: "question",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },

    ...(hasP("faq-status") ? [{
      headerName: t("list_heading_status"),
      field: "status",
      filter: true,
      maxWidth: 70,
      maxWidth: 90,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => {
        const currentStatus = params.value;
        const isActive = currentStatus === STATUS.ACTIVE;
        const newStatus = isActive ? STATUS.INACTIVE : STATUS.ACTIVE;

        return (
          <label className={`custom-switch cp${!isActive ? " e_none" : ""}`}>
            <input
              type="checkbox"
              className="custom-switch-input cp"
              checked={isActive}
              onChange={() => changeStatus(params.data.originalId, newStatus)}
            />
            <span className="custom-switch-indicator cp"></span>
          </label>
        );
      },
    }] : []),
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 130,
      maxWidth: 150,
      valueGetter: (params) => params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) => params.value ? moment(params.value).format("MMMM DD, YYYY") : ""
    },
    ...(hasP("faq-view") || hasP("faq-edit") || hasP("faq-delete") ? [{
      headerName: t("list_heading_action"),
      maxWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("faq-view") && (
                <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}>
                  <button type="button" className="btn view" onClick={() => navigate(`/admin/cms/faq-management/faq/view/${row.originalId}`)}>
                    <i className="ri-eye-fill"></i>
                  </button>
                </Whisper>
              )}
              {hasP("faq-edit") && (
                <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}>
                  <button type="button" className="btn edit" onClick={() => navigate(`/admin/cms/faq-management/faq/edit/${row.originalId}`)}>
                    <i className="ri-pencil-fill"></i>
                  </button>
                </Whisper>
              )}
              {hasP("faq-delete") && (
                <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}>
                  <button type="button" className="btn delete" onClick={() => Deletefunction(row)}>
                    <i className="ri-delete-bin-6-fill"></i>
                  </button>
                </Whisper>
              )}
            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate]);

  // 🔥 ROW DATA - Search Filter
  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];
    return data.data.list
      .map((row) => ({
        originalId: row.id,
        question: row.question ? TrimText(row.question, 70) : "N/A",
        type: row.type
          ? row.type.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
          : "N/A",
        status: row.status,
        createdAt: row.createdAt,
      }))
      .filter((row) =>
        searchData ? row.question.toLowerCase().includes(searchData.toLowerCase()) : true
      );
  }, [data?.data?.list, searchData]);

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    getRowId: (params) => params.data.originalId.toString(),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: { padding: "10px 8px" },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
  }), [rowData, colDefs]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-header d-flex justify-content-between align-items-center" style={{ borderBottom: "none" }}>
            <h6 className="main-content-label">{t("sidebar_link_faq")}</h6>
            <div className="d-flex align-items-center">
              <div style={{ width: 300 }} className="me-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchData}
                  onChange={(e) => setSearchData(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>
              <select
                className="form-select form-control me-3 cp"
                style={{ width: 100 }} onChange={(e) => {
                  const value = e.target.value;
                  if (gridRef.current && gridRef.current.api) {
                    if (value && value.length) {
                      gridRef.current.api.setFilterModel({
                        status: { type: "equals", filter: value }
                      });
                    } else {
                      gridRef.current.api.setFilterModel(null);
                    }
                    gridRef.current.api.onFilterChanged();
                  }
                }}>
                <option value="">{t("list_heading_status")}</option>
                <option value={STATUS.ACTIVE}>{t("btn_active")}</option>
                <option value={STATUS.INACTIVE}>{t("btn_inactive")}</option>
              </select>
              <div className="me-3">
                <CustomProvider>
                  <DateRangePicker
                    style={{ width: "150px" }}
                    placeholder={t("input_placeholder_date")}
                    placement="bottomEnd"
                    onChange={(value) => {
                      if (!gridRef.current || !gridRef.current.api) return;
                      if (!value || value.length !== 2) {
                        gridRef.current.api.setFilterModel(null);
                        gridRef.current.api.onFilterChanged();
                        return;
                      }
                      const [start, end] = value;
                      gridRef.current.api.setFilterModel({
                        createdAt: {
                          type: "inRange",
                          dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                          dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                        }
                      });
                      gridRef.current.api.onFilterChanged();
                    }}
                  />
                </CustomProvider>
              </div>
              {hasP("faq-add") && (
                <button className="btn ripple btn-main-primary signbtn" onClick={() => navigate(`/admin/cms/faq-management/faq/add`)}>
                  {t("btn_add_new")}
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FaqTable;
